import React from "react";

import Goals from "./steps/Goals";
import JoinGoals from "./steps/JoinGoals";
import Superpowers from "./steps/Superpowers";
import MembershipStep from "./steps/MembershipStep";
import PaymentStep from "./steps/PaymentStep";
import CalendlyStep from "./steps/CalendlyStep";
import SignupConfirmation from "./steps/SignupConfirmation";
import Tasks from "./steps/followups/Tasks";
import { StepProps, STEPS, SignupState, StepRef } from "./types";
import ConsultationConfirmation from "./steps/ConsultationConfirmation";
import ConsultationDetails from "./steps/ConsultationDetails";
import ConsultationDetailsShort from "./steps/ConsultationDetailsShort";
import ConsultationConfirmationShort from "./steps/ConsultationConfirmationShort";
import { memoize } from "lodash";
import LifeEvents from "./steps/followups/LifeEvents";
import AnythingElse from "./steps/followups/AnythingElse";
import ReferralSource from "./steps/followups/ReferralSource";
import FamilyInfo from "./steps/followups/FamilyInfo";
import TasksInterrupt from "./steps/TasksInterrupt";
import AboutAdvisorsInterrupt from "./steps/AboutAdvisorsInterrupt";
import CreateAccount from "./steps/CreateAccount";
import PaymentPhoneStep from "./steps/PaymentPhoneStep";
import AnythingElseUpgrade from "./steps/followups/AnythingElseUpgrade";
import GetStartedProfile from "./steps/getStarted/GetStartedProfile";
import GetStartedCreateAccount from "./steps/getStarted/GetStartedCreateAccount";
import GetStartedCongrats from "./steps/getStarted/GetStartedCongrats";
import GetStartedCalendar from "./steps/getStarted/GetStartedCalendar";
import GetStartedAddress from "./steps/getStarted/GetStartedAddress";
import GetStartedPartner from "./steps/getStarted/GetStartedPartner";

export type SignupStep = {
  name: STEPS;
  component: React.ForwardRefExoticComponent<
    StepProps & React.RefAttributes<StepRef>
  >;
  defaultBackStep: STEPS | null;
  nextStep?: (state: SignupState) => STEPS;
  isValid?: (state: SignupState) => boolean;
  completionStateName?:
    | "consultation-scheduled"
    | "signup-complete"
    | "account-creation-complete"
    | "get-started-digital-subscription"
    | "upgrade-success";
  backDisabled?: boolean;
  whiteBackground?: boolean;
  signupTerminus?: boolean; // indicates that "signup" is complete at this step.
  refreshTokenOnComplete?: boolean;
  hideStepIndicator?: boolean; // excluded from the step count, and shows no indicator.
};

const SIGNUP_FLOW: SignupStep[] = [
  // Old steps
  {
    name: STEPS.GOALS,
    component: Goals,
    defaultBackStep: null,
    backDisabled: true,
    nextStep: () => STEPS.TASKS_INTERRUPT,
    isValid: (state) => !!Object.keys(state?.goals || {}).length,
  },
  {
    name: STEPS.TASKS_INTERRUPT,
    component: TasksInterrupt,
    defaultBackStep: STEPS.GOALS,
    nextStep: () => STEPS.SUPERPOWERS,
  },
  {
    name: STEPS.SUPERPOWERS,
    component: Superpowers,
    defaultBackStep: STEPS.TASKS_INTERRUPT,
    nextStep: () => STEPS.ABOUT_ADVISORS_INTERRUPT,
    isValid: (state) => !!Object.keys(state?.superpowers || {}).length,
  },
  {
    name: STEPS.ABOUT_ADVISORS_INTERRUPT,
    component: AboutAdvisorsInterrupt,
    defaultBackStep: STEPS.SUPERPOWERS,
    nextStep: () => STEPS.MEMBERSHIP,
  },
  {
    name: STEPS.MEMBERSHIP,
    component: MembershipStep,
    defaultBackStep: STEPS.ABOUT_ADVISORS_INTERRUPT,
    // Escape path to consultation.
    nextStep: (state: SignupState) =>
      state.flowPath === STEPS.CONSULTATION_DETAILS
        ? STEPS.CONSULTATION_DETAILS
        : STEPS.PAYMENT,
  },

  // Forks here.
  // Branch 1: Self serve
  {
    name: STEPS.PAYMENT,
    component: PaymentStep,
    defaultBackStep: STEPS.MEMBERSHIP,
    nextStep: () => STEPS.SIGNUP_CONFIRMATION,
    isValid: (state) => !!state?.payment?.paymentMethodId,
  },
  {
    name: STEPS.SIGNUP_CONFIRMATION,
    component: SignupConfirmation,
    defaultBackStep: STEPS.PAYMENT,
    backDisabled: true,
    signupTerminus: true,
    completionStateName: "signup-complete",
    nextStep: () => STEPS.FAMILY_INFO,
  },

  // Follow-up questions
  {
    name: STEPS.FAMILY_INFO,
    component: FamilyInfo,
    defaultBackStep: STEPS.SIGNUP_CONFIRMATION,
    nextStep: () => STEPS.LIFE_EVENTS,
  },
  {
    name: STEPS.LIFE_EVENTS,
    component: LifeEvents,
    defaultBackStep: STEPS.FAMILY_INFO,
    nextStep: () => STEPS.TASKS,
  },
  {
    name: STEPS.TASKS,
    component: Tasks,
    defaultBackStep: STEPS.SIGNUP_CONFIRMATION,
    nextStep: () => STEPS.ANYTHING_ELSE,
  },
  // {
  //   name: STEPS.TODO_LIST,
  //   component: TodoList,
  //   defaultBackStep: STEPS.LIFE_EVENTS,
  //   nextStep: () => STEPS.ANYTHING_ELSE,
  // },
  {
    name: STEPS.ANYTHING_ELSE,
    component: AnythingElse,
    defaultBackStep: STEPS.LIFE_EVENTS,
    nextStep: () => STEPS.REFERRAL_SOURCE,
  },
  {
    name: STEPS.REFERRAL_SOURCE,
    component: ReferralSource,
    defaultBackStep: STEPS.ANYTHING_ELSE,
    backDisabled: true,
  },
  // {
  //   name: STEPS.FOLLOWUP_CONFIRMATION,
  //   component: FollowupConfirmation,
  //   defaultBackStep: STEPS.REFERRAL_SOURCE,
  //   backDisabled: true,
  // },

  // Branch 2: Consultation
  {
    name: STEPS.CONSULTATION_DETAILS,
    component: ConsultationDetails,
    defaultBackStep: STEPS.MEMBERSHIP,
    nextStep: () => STEPS.CONSULTATION_TASKS,
    isValid: (state) =>
      !!state?.consultationDetails?.firstName &&
      !!state?.consultationDetails?.lastName &&
      !!state?.consultationDetails?.zipCode &&
      !!state?.consultationDetails?.email &&
      !!state?.consultationDetails?.phone,
  },
  {
    name: STEPS.CONSULTATION_TASKS,
    component: Tasks,
    defaultBackStep: STEPS.CONSULTATION_DETAILS,
    nextStep: () => STEPS.CONSULTATION_SCHEDULE,
  },
  {
    name: STEPS.CONSULTATION_SCHEDULE,
    component: CalendlyStep,
    defaultBackStep: STEPS.CONSULTATION_TASKS,
    isValid: (state) =>
      !!state?.consultation?.inviteeUri && !!state?.consultation?.eventUri,
    completionStateName: "consultation-scheduled",
    nextStep: () => STEPS.CONSULTATION_CONFIRMATION,
    whiteBackground: true,
  },
  {
    name: STEPS.CONSULTATION_CONFIRMATION,
    component: ConsultationConfirmation,
    defaultBackStep: STEPS.CONSULTATION_SCHEDULE,
    signupTerminus: true,
    // NOTE(Kip): We might want to disable this...but lets see if users go back/forth.
    // backDisabled: true,
  },
];

const FREE_SIGNUP_FLOW: SignupStep[] = [
  {
    name: STEPS.GOALS,
    component: JoinGoals,
    defaultBackStep: null,
    backDisabled: true,
    nextStep: () => STEPS.ABOUT_ADVISORS_INTERRUPT,
    isValid: (state) => !!Object.keys(state?.goals || {}).length,
  },
  {
    name: STEPS.ABOUT_ADVISORS_INTERRUPT,
    component: AboutAdvisorsInterrupt,
    defaultBackStep: STEPS.GOALS,
    nextStep: () => STEPS.CREATE_ACCOUNT,
  },
  {
    name: STEPS.CREATE_ACCOUNT,
    component: CreateAccount,
    defaultBackStep: STEPS.ABOUT_ADVISORS_INTERRUPT,
    backDisabled: true,
    completionStateName: "account-creation-complete",
    nextStep: () => STEPS.REFERRAL_SOURCE,
  },
  {
    name: STEPS.REFERRAL_SOURCE,
    component: ReferralSource,
    defaultBackStep: STEPS.ANYTHING_ELSE,
    backDisabled: true,
    signupTerminus: true,
    hideStepIndicator: true,
  },
];

const GET_STARTED_FLOW: SignupStep[] = [
  {
    name: STEPS.GET_STARTED_PROFILE,
    component: GetStartedProfile,
    defaultBackStep: null,
    backDisabled: true,
    nextStep: () => STEPS.GET_STARTED_CREATE_ACCOUNT,
  },
  {
    name: STEPS.GET_STARTED_CREATE_ACCOUNT,
    component: GetStartedCreateAccount,
    defaultBackStep: STEPS.GET_STARTED_PROFILE,
    completionStateName: "get-started-digital-subscription",
    nextStep: () => STEPS.GET_STARTED_CONGRATS,
  },
  {
    name: STEPS.GET_STARTED_CONGRATS,
    component: GetStartedCongrats,
    defaultBackStep: STEPS.GET_STARTED_CREATE_ACCOUNT,
    backDisabled: true,
    nextStep: () => STEPS.GET_STARTED_CALENDAR,
  },
  {
    name: STEPS.GET_STARTED_CALENDAR,
    component: GetStartedCalendar,
    defaultBackStep: STEPS.GET_STARTED_CONGRATS,
    nextStep: () => STEPS.GET_STARTED_ADDRESS,
  },
  {
    name: STEPS.GET_STARTED_ADDRESS,
    component: GetStartedAddress,
    defaultBackStep: STEPS.GET_STARTED_CALENDAR,
    nextStep: () => STEPS.GET_STARTED_PARTNER,
  },
  {
    name: STEPS.GET_STARTED_PARTNER,
    component: GetStartedPartner,
    defaultBackStep: STEPS.GET_STARTED_ADDRESS,
    nextStep: () => STEPS.REFERRAL_SOURCE,
  },
  {
    name: STEPS.REFERRAL_SOURCE,
    component: ReferralSource,
    defaultBackStep: STEPS.GET_STARTED_PARTNER,
    backDisabled: true,
    signupTerminus: true,
  },
];

const UPGRADE_FLOW: SignupStep[] = [
  {
    name: STEPS.MEMBERSHIP,
    component: MembershipStep,
    defaultBackStep: null,
    nextStep: () => STEPS.UPGRADE_PAYMENT,
  },
  {
    name: STEPS.UPGRADE_PAYMENT,
    component: PaymentPhoneStep,
    defaultBackStep: STEPS.MEMBERSHIP,
    nextStep: () => STEPS.FAMILY_INFO,
    isValid: (state) => !!state?.payment?.paymentMethodId,
    completionStateName: "upgrade-success",
    refreshTokenOnComplete: true,
  },
  {
    name: STEPS.FAMILY_INFO,
    component: FamilyInfo,
    defaultBackStep: STEPS.UPGRADE_PAYMENT,
    nextStep: () => STEPS.LIFE_EVENTS,
    backDisabled: true,
  },
  {
    name: STEPS.LIFE_EVENTS,
    component: LifeEvents,
    defaultBackStep: STEPS.FAMILY_INFO,
    nextStep: () => STEPS.TASKS,
  },
  {
    name: STEPS.TASKS,
    component: Tasks,
    defaultBackStep: STEPS.LIFE_EVENTS,
    nextStep: () => STEPS.ANYTHING_ELSE,
  },
  {
    name: STEPS.ANYTHING_ELSE,
    component: AnythingElseUpgrade,
    defaultBackStep: STEPS.TASKS,
    signupTerminus: true,
  },
];

const CONSULTATION_FLOW: SignupStep[] = [
  {
    name: STEPS.CONSULTATION_DETAILS,
    component: ConsultationDetailsShort,
    defaultBackStep: null,
    nextStep: () => STEPS.CONSULTATION_SCHEDULE,
    isValid: (state) =>
      !!state?.consultationDetails?.zipCode &&
      !!state?.consultationDetails?.phone,
  },
  {
    name: STEPS.CONSULTATION_SCHEDULE,
    component: CalendlyStep,
    defaultBackStep: STEPS.CONSULTATION_DETAILS,
    isValid: (state) =>
      !!state?.consultation?.inviteeUri && !!state?.consultation?.eventUri,
    completionStateName: "consultation-scheduled",
    nextStep: () => STEPS.CONSULTATION_CONFIRMATION,
    whiteBackground: true,
  },
  {
    name: STEPS.CONSULTATION_CONFIRMATION,
    component: ConsultationConfirmationShort,
    defaultBackStep: STEPS.CONSULTATION_SCHEDULE,
    signupTerminus: true,
  },
];

const getPathToStep = memoize((flow: SignupStep[], stepName: STEPS) => {
  const stepsByName = flow.reduce(
    (acc, step) => {
      acc[step.name] = step;
      return acc;
    },
    {} as Record<STEPS, SignupStep>,
  );
  const firstStep = stepsByName[stepName];
  if (!firstStep) {
    return [];
  }
  const path: SignupStep[] = [firstStep];
  while (path[0].defaultBackStep) {
    const step = stepsByName[path[0].defaultBackStep];
    path.unshift(step);
  }
  return path;
});

const getLongestPathFromEnd = memoize(
  (
    graph: SignupStep[],
    endAtStep?: STEPS,
    signupTerminus?: boolean,
  ): number => {
    // Create a quick lookup: step name -> step definition
    const stepMap = new Map<STEPS, SignupStep>();
    for (const step of graph) {
      stepMap.set(step.name, step);
    }

    // Identify end states (no nextStep defined)
    const endStates = graph.filter((step) =>
      signupTerminus ? step.signupTerminus : step.nextStep === undefined,
    );

    // Helper: Count how many back-steps from a given end state -> start
    const distanceToStart = (name: STEPS): number | null => {
      let count = 0;
      let current = stepMap.get(name);
      while (current) {
        if (endAtStep && current.name === endAtStep) {
          return count;
        }
        if (!current.defaultBackStep) {
          if (!endAtStep) {
            return count;
          } else {
            return null;
          }
        }
        count++;
        current = stepMap.get(current.defaultBackStep);
      }
      return count;
    };

    // Compute the maximum distance among all end states
    let maxDistance = 0;
    for (const endState of endStates) {
      const dist = distanceToStart(endState.name);
      if (dist && dist > maxDistance) {
        maxDistance = dist;
      }
    }
    return maxDistance;
  },
  (graph, endAtStep, signupTerminus) =>
    JSON.stringify([graph.map((s) => s.name), endAtStep, signupTerminus]), // Custom cache key
);

export {
  SIGNUP_FLOW,
  FREE_SIGNUP_FLOW,
  GET_STARTED_FLOW,
  UPGRADE_FLOW,
  CONSULTATION_FLOW,
  getPathToStep,
  getLongestPathFromEnd,
};
